import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "./../../../components/Button/Button";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import { getAdherentsByUser } from "./../../../services/adherentServices";
import { getDossierInscription } from "./../../../services/dossierServices";
import { getGroupName } from "./../../../services/groupServices";
import { getAllSegments } from "./../../../services/segmentDossierServices";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import AdherentCards from "./AdherentCard";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { ToastContainer } from "react-toastify";
import {
  FaFileDownload,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const Dropdown = ({ title, children, isOpen, toggle }) => (
  <div className="bg-blue-100 rounded-lg shadow mb-4 p-4 max-w-[800px] mx-auto w-full">
    <button
      onClick={toggle}
      className="w-full flex justify-between items-center text-left text-lg font-semibold dark:text-gray-800"
    >
      {title}
      {isOpen ? <FaChevronUp /> : <FaChevronDown />}
    </button>
    {isOpen && <div className="mt-2">{children}</div>}
  </div>
);

const InscriptionAdherent = () => {
  useScrollToTop();
  const [adherents, setAdherents] = useState([]);
  const [dossiers, setDossiers] = useState({});
  const [groupNames, setGroupNames] = useState({});
  const { user } = useUser();
  const navigate = useNavigate();
  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId;

  const [segments, setSegments] = useState([]);
  const [openDropdowns, setOpenDropdowns] = useState({});

  const toggleDropdown = (id) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const loadData = async () => {
    if (!user || !user.userId) {
      console.error("Utilisateur non identifié");
      return;
    }
    try {
      const adherentsData = await getAdherentsByUser(user.userId);
      setAdherents(adherentsData);

      const dossierPromises = adherentsData.map((adherent) =>
        getDossierInscription(adherent.id)
      );
      const dossiersData = await Promise.all(dossierPromises);
      const dossiersObj = adherentsData.reduce((acc, adherent, index) => {
        acc[adherent.id] = dossiersData[index];
        return acc;
      }, {});
      setDossiers(dossiersObj);

      // Load group names for each adherent
      const groupNamesPromises = adherentsData.map((adherent) =>
        getGroupName(adherent.id)
      );
      const groupNamesData = await Promise.all(groupNamesPromises);
      const groupNamesObj = adherentsData.reduce((acc, adherent, index) => {
        acc[adherent.id] = groupNamesData[index];
        return acc;
      }, {});
      setGroupNames(groupNamesObj);
    } catch (error) {
      console.error("Erreur lors de la récupération des données:", error);
    }

    const segmentsData = await getAllSegments();
    setSegments(segmentsData);

    // Par défaut tous les dropdowns sont fermés
    const dropdownStates = {};
    segmentsData.forEach((segment) => {
      dropdownStates[segment.id] = false;
    });
    setOpenDropdowns(dropdownStates);
  };

  useEffect(() => {
    loadData();
  }, [user]);

  const gridClass =
    adherents.length === 1
      ? "grid-cols-1 place-content-center"
      : "grid-cols-1 md:grid-cols-2 lg:grid-cols-2";

  const handleDownload = (fileUrl) => {
    window.open(fileUrl, "_blank");
  };

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light tracking-[0.4em] text-gray-800 text-center dark:text-white mb-4">
        UTILISATEURS
      </h2>
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-widest text-gray-800 text-center dark:text-white mb-4 font-montserrat uppercase">
        Dossier d'Inscription
      </h2>
      <DashboardNavigation role={userRole} />

      <div className="text-center p-4 bg-blue-100 rounded-lg shadow max-w-[800px] mx-auto mb-4 dark:text-gray-800 font-montserrat">
        <p className="text-lg font-semibold mb-2">
          Bienvenue sur la page : Dossier d'Inscription
        </p>
        <p className="text-sm text-left">
          Sur cette page, vous pouvez consulter les dossiers d'inscription de
          vos adhérents, mais également créer un nouvel adhérent.
        </p>
        <p className="text-lg font-semibold mt-4">
          Vous gérez actuellement {adherents.length} adhérent(s).
        </p>
      </div>

      {adherents.length > 0 && (
        <div className="overflow-x-auto max-w-[800px] mx-auto bg-blue-100 mb-5 shadow-md">
          <table className="table-auto min-w-full bg-blue-100 dark:bg-gray-800 divide-y divide-gray-200 font-montserrat text-sm">
            <thead className="bg-blue-200 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Photo
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom Prénom
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut Inscription
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Saison Active
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Groupe
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {adherents.map((adherent) => {
                const defaultImageUrl =
                  adherent.genre === "Masculin"
                    ? "/img/judoka_avatar.png"
                    : "/img/judokate_avatar.png";
                return (
                  <tr key={adherent.id}>
                    <td className="px-4 py-2">
                      <img
                        src={adherent.photo_url || defaultImageUrl}
                        alt="Photo de l'adhérent"
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    </td>
                    <td className="px-4 py-2">
                      {adherent.nom} {adherent.prenom}
                    </td>
                    <td className="px-4 py-2">{adherent.statut_inscription}</td>
                    <td className="px-4 py-2">{adherent.saison_active}</td>
                    <td className="px-4 py-2">
                      {groupNames[adherent.id]?.join(", ") || "Non attribué"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-col items-center md:flex-row justify-center text-black max-w-[1280px] mx-auto w-full gap-4 mb-4">
        <Button
          text="Création d'un nouvel adhérent"
          onClick={() => navigate("/ajouter-inscription-adherent")}
          hoverLabel="Créer un nouvel adhérent"
        />
      </div>

      {segments.map((segment) => (
        <Dropdown
          key={segment.id}
          title={segment.segment_name}
          isOpen={openDropdowns[segment.id]}
          toggle={() => toggleDropdown(segment.id)}
        >
          <div
            className="text-sm text-left font-montserrat dark:text-gray-800"
            dangerouslySetInnerHTML={{ __html: segment.segment_content }}
          />
          {segment.fichiers?.length > 0 && (
            <div className="mt-4 flex flex-col gap-2 items-center">
              {segment.fichiers.map((file) => (
                <Button
                  key={file.id}
                  text={file.nom_fichier}
                  icon={FaFileDownload}
                  onClick={() => handleDownload(file.lien_fichier)}
                  hoverLabel={file.detail_fichier}
                  className="gap-2"
                />
              ))}
            </div>
          )}
        </Dropdown>
      ))}

      <div className={`flex flex-col w-full mx-auto mb-4 p-2 dark:text-white`}>
        <div className={`grid ${gridClass} gap-6 max-w-[1280px] mx-auto`}>
          {adherents.map((adherent) => (
            <div
              key={adherent.id}
              className="flex flex-col gap-4 bg-base-100 dark:bg-gray-800 p-4 rounded-lg shadow-md"
            >
              <AdherentCards
                adherent={adherent}
                dossier={dossiers[adherent.id]}
                user={user}
                refreshAdherents={loadData} // Passer loadData comme prop pour rafraîchir les adhérents
              />
            </div>
          ))}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default InscriptionAdherent;
